<template>
  <v-col class="sign-in align-center justify-center">
    <v-row class="text-center darkestblue--text">
      <v-col>
        <v-row>
          <v-col>
            <v-img
              class="ma-auto"
              :src="logoDarkMode"
              alt="Logo for adops.com"
              height="auto"
              max-width="60px"
            />
          </v-col>
        </v-row>

        <v-row class="white--text">
          <v-col>
            <!--- Application Title -->
            <v-row class="sign-in-title display-1">
              <v-col>
                <h1 class="ma-0 pa-0">
                  iAdOps
                </h1>
              </v-col>
            </v-row>

            <!-- Description -->
            <v-row class="description">
              <v-col>
                <p class="ma-0">
                  iAdOps is adops.com's ad operations platform, giving
                  publishers and our ad operations team a single, secure
                  interface to monitor ad delivery pacing, reconcile
                  discrepancies, and track revenue performance across
                  advertising networks like Google Ad Manager.
                </p>
              </v-col>
            </v-row>

            <!-- "Sign In" Button -->
            <v-row class="justify-center mt-6">
              <v-col class="col-auto">
                <v-btn
                  color="darkblue"
                  dark
                  style="width: 290px"
                  @click="goToSignIn"
                >
                  <!--
                    "darkblue" (not the theme's "primary") is used
                    deliberately - white text on "primary" (#0094ff)
                    computes to ~3.15:1 contrast, below the WCAG AA 4.5:1
                    threshold for normal-size text. "darkblue" (#0062a8)
                    computes to ~6.3:1 with white text, comfortably
                    passing, while staying a clear, vivid, on-brand blue.
                    The "dark" prop forces white text - Vuetify's own
                    auto-contrast guess for this custom theme color
                    picked near-black text instead, which is worse
                    (~3.1:1), and a plain white--text class on the
                    label wasn't specific enough to override it.
                  -->
                  <span class="font-weight-bold">Sign In</span>
                </v-btn>
              </v-col>
            </v-row>

            <!-- Divider -->
            <v-row class="justify-center mt-8">
              <v-col style="max-width: 320px">
                <v-divider />
              </v-col>
            </v-row>

            <!-- Google Data Usage Disclosure -->
            <v-row class="fine-print justify-center mt-4">
              <v-col style="max-width: 400px">
                <p class="ma-0">
                  Signing in with Google is used only to verify your
                  identity and grant secure access to iAdOps. We
                  don't access your Gmail, files, or other personal Google
                  data beyond your basic profile and email.
                </p>
              </v-col>
            </v-row>

            <!--- Footer -->
            <v-row class="justify-center mt-4">
              <v-col style="max-width: 320px">
                <v-row>
                  <!-- Privacy Policy / Terms of Service / Help -->
                  <v-col class="pt-0">
                    <a
                      class="caption font-weight-medium white--text"
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener"
                    >Privacy Policy</a>
                    <span class="caption white--text px-1">&middot;</span>
                    <a
                      class="caption font-weight-medium white--text"
                      href="/terms-of-service"
                      target="_blank"
                      rel="noopener"
                    >Terms of Service</a>
                    <span class="caption white--text px-1">&middot;</span>
                    <span
                      class="caption font-weight-medium white--text cursor-pointer"
                      @click="showHelpView = !showHelpView"
                    >Help</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- "Help" Dialog -->
    <v-dialog
      v-model="showHelpView"
      width="420px"
    >
      <v-card>
        <v-card-text>
          <v-col>
            <v-row class="pt-4">
              <v-col>
                <h3 class="headline">
                  Help
                </h3>
              </v-col>

              <v-col class="col-auto mt-1">
                <v-btn
                  :ripple="false"
                  icon
                  small
                  @click="showHelpView = false"
                >
                  <font-awesome-icon
                    :icon="['fas', 'xmark']"
                    style="color: black;"
                  />
                </v-btn>
              </v-col>
            </v-row>

            <v-row class="mt-0">
              <v-col>
                <span class="font-weight-medium">
                  Email our Help Desk at
                  <a
                    class="font-weight-bold"
                    href="mailto:tech@adops.com"
                  >tech@adops.com</a>.
                </span>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-divider />
              </v-col>
            </v-row>

            <v-row class="mt-0">
              <v-col>
                <p class="mb-0 caption font-weight-medium">
                  Release {{ version }}
                </p>
              </v-col>
            </v-row>
          </v-col>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapState: mapLayoutState } = createNamespacedHelpers('layout')

export default {
  name: 'Welcome',
  data() {
    return {
      showHelpView: false,
    }
  },
  computed: {
    ...mapLayoutState({
      navIcons: (state) => state.icons.navigation,
      version: (state) => state.version,
    }),
    logoDarkMode() {
      return this.navIcons.logoDarkMode
    },
  },
  methods: {
    goToSignIn() {
      this.$router.push('/sign-in')
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.sign-in {
  background: linear-gradient(0deg, #0b3d6f 0%, $color-monochrome-nearblack 100%);
  height: 100%;
  min-height: 100vh;
  padding: 120px 100px;

  @media screen and (min-width: $vuetify-sm-min-size) and (max-width: $vuetify-md-max-size) {
    padding: 10%;
  }

  @media screen and (max-width: $vuetify-xs-max-size) {
    padding: 10px;
  }
}

.sign-in-title h1 {
  // 34px, not 3rem/48px: matches Vuetify's "display-1" utility class,
  // which is what actually governed this text's size before it became
  // a real <h1> - a bare <span> here had no element-specific rule to
  // out-specificity display-1's single class selector, so display-1
  // won by source order. An h1 selector has higher specificity and
  // would otherwise override it, rendering visibly larger.
  font-size: 34px;
  font-weight: 500;
  letter-spacing: 1.71px;
  line-height: 49px;
}

.description {
  margin: 1.5rem auto 0;
  max-width: 60ch;
  font-size: 1.25ch;
  font-weight: 500;
  letter-spacing: 0.86px;
  line-height: 25px;
}

.fine-print {
  font-size: 1ch;
  font-weight: 400;
  line-height: 20px;
  opacity: 0.75;
}
</style>
